/**
 * Virtualized spreadsheet surface (adapted from SolveSheets, styled for Crewline).
 *
 * Layout: one scroll container; a sticky header block (column letters, an optional per-column
 * "band" slot — used by the importer for field mapping — and optionally the frozen first data row)
 * plus absolutely-positioned virtualized body rows. Cells inside a row are in flow (flex) so the
 * row-number gutter and pinned columns can use position:sticky; rows are positioned with `top`,
 * never `transform`, because transforms break sticky descendants.
 *
 * Excel interaction model: click/drag/Shift range selection, header row/column selection,
 * F2 / double-click / type-to-edit, Enter/Tab commit-and-move, Ctrl+C/X/V (Excel-quoted TSV through
 * native clipboard events), Ctrl+D/R fill, Ctrl+Z/Y undo, Ctrl+F find & replace, Ctrl+arrow data-edge
 * jumps, column resize + autofit, per-column value filters (view-only), sort, freeze panes.
 *
 * Decorations: the host can mark cells (error / warning / info) and rows (gutter badge) — the
 * importer uses this for live validation.
 */
import {
  ArrowDownAZIcon,
  ArrowUpAZIcon,
  BetweenHorizonalStartIcon,
  BetweenVerticalStartIcon,
  ChevronDownIcon,
  CopyXIcon,
  EraserIcon,
  FilterIcon,
  ListFilterIcon,
  PinIcon,
  PinOffIcon,
  ReplaceIcon,
  RulerIcon,
  SearchIcon,
  Trash2Icon,
  XIcon,
} from 'lucide-react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuSeparator, ContextMenuTrigger } from '@/components/ui/context-menu';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import {
  autofitColWidth,
  cellKey,
  cellRef,
  colLabel,
  colWidth,
  columnValues,
  dataColCount,
  deleteCols,
  deleteRowSet,
  displayDims,
  EXTRA_ROWS,
  getCell,
  HEADER_H,
  insertCols,
  insertRows,
  parseClipboardGrid,
  ROW_H,
  ROW_NUM_W,
  selectionStats,
  setCell,
  setCells,
  setColWidth,
  sortByColumn,
  visibleDataRows,
  type CellIssue,
  type RowMark,
  type SelectionStats,
  type SheetData,
} from './sheet-model';

export interface SheetGridHandle {
  openFind: () => void;
  focus: () => void;
  scrollToRow: (dataRow: number) => void;
}

export interface SelectionInfo {
  ref: string;
  activeValue: string;
  stats: SelectionStats | null;
  rows: number;
  cols: number;
  /** Data row / column of the active cell. */
  activeRow: number;
  activeCol: number;
}

export interface SheetDecorations {
  /** cellKey(dataRow, col) → issue. */
  cells?: Map<string, CellIssue>;
  /** dataRow → gutter mark. */
  rows?: Map<number, RowMark>;
}

export interface ColumnBand {
  height: number;
  render: (col: number, width: number) => ReactNode;
}

interface Props {
  sheet: SheetData;
  apply: (fn: (s: SheetData) => SheetData, opts?: { undoable?: boolean }) => void;
  onUndo?: () => void;
  onRedo?: () => void;
  onSelectionInfo?: (info: SelectionInfo) => void;
  readOnly?: boolean;
  /** Show blank rows/columns past the data (editable sheets). */
  openEnded?: boolean;
  decorations?: SheetDecorations;
  /** Extra sticky row under the column letters (e.g. field-mapping dropdowns). */
  columnBand?: ColumnBand;
  /** Restrict visible data rows (e.g. "only problems"); combined with column filters. */
  rowPredicate?: (dataRow: number) => boolean;
  /** Per-column header title shown instead of the letter (e.g. detected header text). */
  className?: string;
}

/** Selection in VIEW coordinates (filters remap rows). */
interface Sel {
  ar: number;
  ac: number;
  fr: number;
  fc: number;
}

const OVERSCAN_ROWS = 6;
const OVERSCAN_COLS = 3;
const STATS_CAP = 100_000;

export const SheetGrid = forwardRef<SheetGridHandle, Props>(function SheetGrid(
  { sheet, apply, onUndo, onRedo, onSelectionInfo, readOnly, openEnded = !readOnly, decorations, columnBand, rowPredicate, className },
  ref,
) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<HTMLInputElement | null>(null);
  const [scroll, setScroll] = useState({ top: 0, left: 0 });
  const [viewport, setViewport] = useState({ w: 800, h: 400 });
  const [sel, setSel] = useState<Sel>({ ar: 0, ac: 0, fr: 0, fc: 0 });
  const [editing, setEditing] = useState<{ vr: number; c: number } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [dragging, setDragging] = useState(false);
  const [ctxTarget, setCtxTarget] = useState<{ kind: 'cell' | 'row' | 'col'; vr: number; c: number } | null>(null);
  const [find, setFind] = useState<{ open: boolean; q: string; rep: string; matchCase: boolean }>({ open: false, q: '', rep: '', matchCase: false });
  const findInputRef = useRef<HTMLInputElement | null>(null);

  // ── view mapping (filters + frozen header + host predicate) ────────────────
  const filtered = Object.keys(sheet.filters).length > 0 || !!rowPredicate;
  const bodyRows = useMemo<number[] | null>(() => {
    const mapped = visibleDataRows(sheet);
    const skipHeader = sheet.frozenHeader && sheet.rows.length > 0;
    let base: number[] | null;
    if (mapped === null) base = skipHeader ? sheet.rows.map((_, i) => i).slice(1) : null;
    else base = skipHeader ? mapped.filter((r) => r !== 0) : mapped;
    if (!rowPredicate) return base;
    const all = base ?? sheet.rows.map((_, i) => i).slice(skipHeader ? 1 : 0);
    return all.filter((r) => rowPredicate(r));
  }, [sheet, rowPredicate]);

  const headerDataRow = sheet.frozenHeader && sheet.rows.length > 0 ? 0 : null;
  const dims = displayDims(sheet, filtered, openEnded);
  const bodyRowCount = bodyRows === null ? Math.max(dims.rows - (headerDataRow !== null ? 1 : 0), 1) : Math.max(bodyRows.length + (filtered || !openEnded ? 0 : EXTRA_ROWS), 1);
  const cols = dims.cols;

  /** view row → data row (beyond-data rows extrapolate when unfiltered). */
  const viewToData = useCallback(
    (vr: number): number => {
      if (bodyRows === null) return vr + (headerDataRow !== null ? 1 : 0);
      if (vr < bodyRows.length) return bodyRows[vr] as number;
      return sheet.rows.length + (vr - bodyRows.length);
    },
    [bodyRows, headerDataRow, sheet.rows.length],
  );

  const dataToView = useCallback(
    (dr: number): number => {
      if (bodyRows === null) return dr - (headerDataRow !== null ? 1 : 0);
      const i = bodyRows.indexOf(dr);
      return i >= 0 ? i : 0;
    },
    [bodyRows, headerDataRow],
  );

  // ── geometry ────────────────────────────────────────────────────────────
  const widths = useMemo(() => Array.from({ length: cols }, (_, c) => colWidth(sheet, c)), [sheet, cols]);
  const colX = useMemo(() => {
    const xs = new Array<number>(cols + 1);
    xs[0] = 0;
    for (let c = 0; c < cols; c++) xs[c + 1] = (xs[c] as number) + (widths[c] as number);
    return xs;
  }, [widths, cols]);
  const pinned = Math.min(sheet.pinnedCols, cols);
  const pinnedW = colX[pinned] as number;
  const totalW = ROW_NUM_W + (colX[cols] as number);
  const bandH = columnBand?.height ?? 0;
  const headerBlockH = HEADER_H + bandH + (headerDataRow !== null ? ROW_H : 0);
  const totalH = headerBlockH + bodyRowCount * ROW_H;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setViewport({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    setViewport({ w: el.clientWidth, h: el.clientHeight });
    return () => ro.disconnect();
  }, []);

  const firstRow = Math.max(0, Math.floor(scroll.top / ROW_H) - OVERSCAN_ROWS);
  const lastRow = Math.min(bodyRowCount - 1, Math.ceil((scroll.top + viewport.h - headerBlockH) / ROW_H) + OVERSCAN_ROWS);

  const [firstCol, lastCol] = useMemo(() => {
    let f = pinned,
      l = cols - 1;
    const left = scroll.left,
      right = scroll.left + viewport.w;
    for (let c = pinned; c < cols; c++) {
      if (ROW_NUM_W + (colX[c + 1] as number) > left) {
        f = Math.max(pinned, c - OVERSCAN_COLS);
        break;
      }
    }
    for (let c = f; c < cols; c++) {
      if (ROW_NUM_W + (colX[c] as number) > right) {
        l = Math.min(cols - 1, c + OVERSCAN_COLS);
        break;
      }
    }
    return [f, l];
  }, [scroll.left, viewport.w, colX, cols, pinned]);

  // ── selection helpers ───────────────────────────────────────────────────
  const selBounds = useMemo(
    () => ({ r1: Math.min(sel.ar, sel.fr), r2: Math.max(sel.ar, sel.fr), c1: Math.min(sel.ac, sel.fc), c2: Math.max(sel.ac, sel.fc) }),
    [sel],
  );

  const isSelected = (vr: number, c: number) => vr >= selBounds.r1 && vr <= selBounds.r2 && c >= selBounds.c1 && c <= selBounds.c2;

  const activeData = useMemo(() => ({ r: viewToData(sel.ar), c: sel.ac }), [viewToData, sel.ar, sel.ac]);
  const activeValue = getCell(sheet, activeData.r, activeData.c);

  useEffect(() => {
    if (!onSelectionInfo) return;
    const { r1, r2, c1, c2 } = selBounds;
    const nRows = r2 - r1 + 1,
      nCols = c2 - c1 + 1;
    let stats: SelectionStats | null = null;
    if (nRows * nCols > 1) {
      const cells = function* (): Generator<[number, number]> {
        let n = 0;
        for (let vr = r1; vr <= r2; vr++) {
          const dr = viewToData(vr);
          for (let c = c1; c <= c2; c++) {
            if (++n > STATS_CAP) return;
            yield [dr, c];
          }
        }
      };
      stats = selectionStats(sheet, cells());
    }
    onSelectionInfo({ ref: cellRef(activeData.r, activeData.c), activeValue, stats, rows: nRows, cols: nCols, activeRow: activeData.r, activeCol: activeData.c });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selBounds, sheet, activeData.r, activeData.c]);

  useEffect(() => {
    setSel((s) => {
      const maxR = Math.max(0, bodyRowCount - 1),
        maxC = cols - 1;
      const cl = (v: number, m: number) => Math.max(0, Math.min(v, m));
      const next = { ar: cl(s.ar, maxR), fr: cl(s.fr, maxR), ac: cl(s.ac, maxC), fc: cl(s.fc, maxC) };
      return next.ar === s.ar && next.fr === s.fr && next.ac === s.ac && next.fc === s.fc ? s : next;
    });
  }, [bodyRowCount, cols]);

  const scrollCellIntoView = useCallback(
    (vr: number, c: number) => {
      const el = containerRef.current;
      if (!el) return;
      const top = headerBlockH + vr * ROW_H,
        bottom = top + ROW_H;
      const viewTop = el.scrollTop + headerBlockH,
        viewBottom = el.scrollTop + el.clientHeight;
      if (top < viewTop) el.scrollTop = top - headerBlockH;
      else if (bottom > viewBottom) el.scrollTop = bottom - el.clientHeight;
      if (c >= pinned) {
        const left = ROW_NUM_W + (colX[c] as number),
          right = ROW_NUM_W + (colX[c + 1] as number);
        const cover = ROW_NUM_W + pinnedW;
        if (left < el.scrollLeft + cover) el.scrollLeft = left - cover;
        else if (right > el.scrollLeft + el.clientWidth) el.scrollLeft = right - el.clientWidth;
      }
    },
    [headerBlockH, colX, pinned, pinnedW],
  );

  const moveTo = useCallback(
    (vr: number, c: number, extend: boolean) => {
      const nvr = Math.max(0, Math.min(vr, bodyRowCount - 1));
      const nc = Math.max(0, Math.min(c, cols - 1));
      setSel((s) => (extend ? { ...s, fr: nvr, fc: nc } : { ar: nvr, ac: nc, fr: nvr, fc: nc }));
      scrollCellIntoView(nvr, nc);
    },
    [bodyRowCount, cols, scrollCellIntoView],
  );

  // ── editing ─────────────────────────────────────────────────────────────
  const startEdit = useCallback(
    (vr: number, c: number, initial?: string) => {
      if (readOnly) return;
      setEditing({ vr, c });
      setEditValue(initial ?? getCell(sheet, viewToData(vr), c));
      scrollCellIntoView(vr, c);
    },
    [readOnly, sheet, viewToData, scrollCellIntoView],
  );

  useEffect(() => {
    if (editing) editorRef.current?.focus();
  }, [editing]);

  const commitEdit = useCallback(
    (move?: 'down' | 'up' | 'right' | 'left') => {
      if (!editing) return;
      const { vr, c } = editing;
      const dr = viewToData(vr);
      if (getCell(sheet, dr, c) !== editValue) apply((s) => setCell(s, dr, c, editValue));
      setEditing(null);
      containerRef.current?.focus();
      if (move === 'down') moveTo(vr + 1, c, false);
      else if (move === 'up') moveTo(vr - 1, c, false);
      else if (move === 'right') moveTo(vr, c + 1, false);
      else if (move === 'left') moveTo(vr, c - 1, false);
      else moveTo(vr, c, false);
    },
    [editing, editValue, sheet, apply, viewToData, moveTo],
  );

  const cancelEdit = useCallback(() => {
    setEditing(null);
    containerRef.current?.focus();
  }, []);

  // ── clipboard ───────────────────────────────────────────────────────────
  const copySelection = useCallback(
    (e: React.ClipboardEvent) => {
      const { r1, r2, c1, c2 } = selBounds;
      const lines: string[] = [];
      for (let vr = r1; vr <= r2; vr++) {
        const dr = viewToData(vr);
        const parts: string[] = [];
        for (let c = c1; c <= c2; c++) {
          const v = getCell(sheet, dr, c);
          parts.push(/[\t\n\r"]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
        }
        lines.push(parts.join('\t'));
      }
      e.clipboardData.setData('text/plain', lines.join('\n'));
      e.preventDefault();
    },
    [selBounds, sheet, viewToData],
  );

  const clearSelection = useCallback(() => {
    if (readOnly) return;
    const { r1, r2, c1, c2 } = selBounds;
    const writes: [number, number, string][] = [];
    for (let vr = r1; vr <= r2; vr++) {
      const dr = viewToData(vr);
      if (dr >= sheet.rows.length) continue;
      for (let c = c1; c <= c2; c++) if (getCell(sheet, dr, c) !== '') writes.push([dr, c, '']);
    }
    if (writes.length > 0) apply((s) => setCells(s, writes));
  }, [readOnly, selBounds, sheet, apply, viewToData]);

  const pasteGrid = useCallback(
    (text: string) => {
      if (readOnly || text === '') return;
      const grid = parseClipboardGrid(text);
      const writes: [number, number, string][] = [];
      for (let i = 0; i < grid.length; i++) {
        const dr = viewToData(selBounds.r1 + i);
        const line = grid[i] as string[];
        for (let j = 0; j < line.length; j++) writes.push([dr, selBounds.c1 + j, line[j] as string]);
      }
      apply((s) => setCells(s, writes));
      moveTo(selBounds.r1 + grid.length - 1, selBounds.c1 + (grid[0]?.length ?? 1) - 1, true);
      setSel((s) => ({ ...s, ar: selBounds.r1, ac: selBounds.c1 }));
    },
    [readOnly, selBounds, apply, viewToData, moveTo],
  );

  // ── find & replace ──────────────────────────────────────────────────────
  const openFind = useCallback(() => {
    setFind((f) => ({ ...f, open: true }));
    setTimeout(() => findInputRef.current?.focus(), 0);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      openFind,
      focus: () => containerRef.current?.focus(),
      scrollToRow: (dr: number) => {
        const vr = dataToView(dr);
        moveTo(vr, sel.ac, false);
      },
    }),
    [openFind, dataToView, moveTo, sel.ac],
  );

  const matches = useCallback(
    (v: string): boolean => {
      if (find.q === '') return false;
      return find.matchCase ? v.includes(find.q) : v.toLowerCase().includes(find.q.toLowerCase());
    },
    [find.q, find.matchCase],
  );

  const findNext = useCallback(
    (backwards = false) => {
      if (find.q === '') return;
      const total = bodyRows === null ? sheet.rows.length : bodyRows.length;
      const width = dataColCount(sheet);
      const step = backwards ? -1 : 1;
      let vr = sel.ar,
        c = sel.ac;
      for (let n = 0; n < total * Math.max(width, 1) + 1; n++) {
        c += step;
        if (c >= width) {
          c = 0;
          vr++;
        }
        if (c < 0) {
          c = width - 1;
          vr--;
        }
        if (vr >= total) vr = 0;
        if (vr < 0) vr = total - 1;
        if (matches(getCell(sheet, viewToData(vr), c))) {
          moveTo(vr, c, false);
          return;
        }
      }
    },
    [find.q, matches, sheet, sel, bodyRows, viewToData, moveTo],
  );

  const replaceCurrent = useCallback(() => {
    if (readOnly) return;
    const v = getCell(sheet, activeData.r, activeData.c);
    if (matches(v)) {
      const rx = new RegExp(escapeRegExp(find.q), find.matchCase ? 'g' : 'gi');
      apply((s) => setCell(s, activeData.r, activeData.c, v.replace(rx, find.rep)));
    }
    findNext();
  }, [readOnly, sheet, activeData, matches, find, apply, findNext]);

  const replaceAll = useCallback(() => {
    if (readOnly || find.q === '') return;
    const rx = new RegExp(escapeRegExp(find.q), find.matchCase ? 'g' : 'gi');
    const writes: [number, number, string][] = [];
    for (let r = 0; r < sheet.rows.length; r++) {
      for (let c = 0; c < (sheet.rows[r]?.length ?? 0); c++) {
        const v = getCell(sheet, r, c);
        if (matches(v)) writes.push([r, c, v.replace(rx, find.rep)]);
      }
    }
    if (writes.length > 0) apply((s) => setCells(s, writes));
  }, [readOnly, find, matches, sheet, apply]);

  // ── keyboard ────────────────────────────────────────────────────────────
  const dataEdgeJump = useCallback(
    (dr: 'up' | 'down' | 'left' | 'right'): [number, number] => {
      const lastDataRow = (bodyRows === null ? sheet.rows.length : bodyRows.length) - 1;
      const width = Math.max(dataColCount(sheet) - 1, 0);
      let vr = sel.fr,
        c = sel.fc;
      const stepR = dr === 'up' ? -1 : dr === 'down' ? 1 : 0;
      const stepC = dr === 'left' ? -1 : dr === 'right' ? 1 : 0;
      const filledHere = getCell(sheet, viewToData(vr), c) !== '';
      for (;;) {
        const nvr = vr + stepR,
          nc = c + stepC;
        if (nvr < 0 || nvr > Math.max(lastDataRow, 0) || nc < 0 || nc > width) break;
        const filled = getCell(sheet, viewToData(nvr), nc) !== '';
        if (filledHere && !filled) break;
        vr = nvr;
        c = nc;
        if (!filledHere && filled) break;
      }
      return [vr, c];
    },
    [sel, sheet, bodyRows, viewToData],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (editing) return;
      const ctrl = e.ctrlKey || e.metaKey;
      const k = e.key;
      const pageRows = Math.max(1, Math.floor((viewport.h - headerBlockH) / ROW_H) - 1);
      const nav = (vr: number, c: number) => {
        e.preventDefault();
        moveTo(vr, c, e.shiftKey);
      };

      if (ctrl && (k === 'z' || k === 'Z')) {
        e.preventDefault();
        if (e.shiftKey) onRedo?.();
        else onUndo?.();
        return;
      }
      if (ctrl && (k === 'y' || k === 'Y')) {
        e.preventDefault();
        onRedo?.();
        return;
      }
      if (ctrl && (k === 'f' || k === 'F')) {
        e.preventDefault();
        openFind();
        return;
      }
      if (ctrl && (k === 'a' || k === 'A')) {
        e.preventDefault();
        setSel({ ar: 0, ac: 0, fr: Math.max(bodyRowCount - 1, 0), fc: cols - 1 });
        return;
      }
      if (ctrl && (k === 'd' || k === 'D')) {
        e.preventDefault();
        if (readOnly) return;
        const { r1, r2, c1, c2 } = selBounds;
        const writes: [number, number, string][] = [];
        for (let c = c1; c <= c2; c++) {
          const v = getCell(sheet, viewToData(r1), c);
          for (let vr = r1 + 1; vr <= r2; vr++) writes.push([viewToData(vr), c, v]);
        }
        if (writes.length) apply((s) => setCells(s, writes));
        return;
      }
      if (ctrl && (k === 'r' || k === 'R')) {
        e.preventDefault();
        if (readOnly) return;
        const { r1, r2, c1, c2 } = selBounds;
        const writes: [number, number, string][] = [];
        for (let vr = r1; vr <= r2; vr++) {
          const dr = viewToData(vr);
          const v = getCell(sheet, dr, c1);
          for (let c = c1 + 1; c <= c2; c++) writes.push([dr, c, v]);
        }
        if (writes.length) apply((s) => setCells(s, writes));
        return;
      }

      switch (k) {
        case 'ArrowUp':
          if (ctrl) {
            const [vr, c] = dataEdgeJump('up');
            nav(vr, c);
          } else nav(sel.fr - 1, sel.fc);
          return;
        case 'ArrowDown':
          if (ctrl) {
            const [vr, c] = dataEdgeJump('down');
            nav(vr, c);
          } else nav(sel.fr + 1, sel.fc);
          return;
        case 'ArrowLeft':
          if (ctrl) {
            const [vr, c] = dataEdgeJump('left');
            nav(vr, c);
          } else nav(sel.fr, sel.fc - 1);
          return;
        case 'ArrowRight':
          if (ctrl) {
            const [vr, c] = dataEdgeJump('right');
            nav(vr, c);
          } else nav(sel.fr, sel.fc + 1);
          return;
        case 'Home':
          nav(ctrl ? 0 : sel.fr, 0);
          return;
        case 'End': {
          const lastC = Math.max(dataColCount(sheet) - 1, 0);
          nav(ctrl ? Math.max((bodyRows === null ? sheet.rows.length : bodyRows.length) - 1, 0) : sel.fr, lastC);
          return;
        }
        case 'PageUp':
          nav(sel.fr - pageRows, sel.fc);
          return;
        case 'PageDown':
          nav(sel.fr + pageRows, sel.fc);
          return;
        case 'Tab':
          e.preventDefault();
          moveTo(sel.fr, sel.fc + (e.shiftKey ? -1 : 1), false);
          return;
        case 'Enter':
          e.preventDefault();
          moveTo(sel.fr + (e.shiftKey ? -1 : 1), sel.fc, false);
          return;
        case 'F2':
          e.preventDefault();
          startEdit(sel.ar, sel.ac);
          return;
        case 'Delete':
        case 'Backspace':
          e.preventDefault();
          clearSelection();
          return;
        case 'Escape':
          setFind((f) => ({ ...f, open: false }));
          return;
      }

      if (!ctrl && k.length === 1 && !e.altKey) {
        e.preventDefault();
        startEdit(sel.ar, sel.ac, k);
      }
    },
    [editing, viewport.h, headerBlockH, moveTo, onUndo, onRedo, openFind, bodyRowCount, cols, readOnly, selBounds, sheet, viewToData, apply, sel, dataEdgeJump, startEdit, clearSelection, bodyRows],
  );

  // ── mouse ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!dragging) return;
    const up = () => setDragging(false);
    window.addEventListener('mouseup', up);
    return () => window.removeEventListener('mouseup', up);
  }, [dragging]);

  const cellMouseDown = (e: React.MouseEvent, vr: number, c: number) => {
    if (e.button === 2 && isSelected(vr, c)) return;
    if (editing) commitEdit();
    e.preventDefault();
    containerRef.current?.focus();
    moveTo(vr, c, e.shiftKey);
    if (e.button === 0) setDragging(true);
  };

  const cellMouseEnter = (vr: number, c: number) => {
    if (dragging) setSel((s) => ({ ...s, fr: vr, fc: c }));
  };

  // ── column resize ───────────────────────────────────────────────────────
  const resizeRef = useRef<{ c: number; startX: number; startW: number } | null>(null);
  const startResize = (e: React.MouseEvent, c: number) => {
    e.preventDefault();
    e.stopPropagation();
    resizeRef.current = { c, startX: e.clientX, startW: widths[c] as number };
    const move = (me: MouseEvent) => {
      const r = resizeRef.current;
      if (!r) return;
      apply((s) => setColWidth(s, r.c, r.startW + (me.clientX - r.startX)), { undoable: false });
    };
    const up = () => {
      resizeRef.current = null;
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  // ── structure ops ───────────────────────────────────────────────────────
  const targetDataRows = (): number[] => {
    const { r1, r2 } = selBounds;
    const out: number[] = [];
    for (let vr = r1; vr <= r2; vr++) {
      const dr = viewToData(vr);
      if (dr < sheet.rows.length) out.push(dr);
    }
    return out;
  };

  const doInsertRows = (where: 'above' | 'below') => {
    const drs = targetDataRows();
    const at = where === 'above' ? (drs[0] ?? viewToData(selBounds.r1)) : (drs[drs.length - 1] ?? viewToData(selBounds.r2)) + 1;
    apply((s) => insertRows(s, at, selBounds.r2 - selBounds.r1 + 1));
  };

  const doDeleteRows = () => {
    const set = new Set(targetDataRows());
    if (set.size === 0) return;
    apply((s) => deleteRowSet(s, set));
  };

  const doInsertCols = (where: 'left' | 'right') => {
    const at = where === 'left' ? selBounds.c1 : selBounds.c2 + 1;
    apply((s) => insertCols(s, at, selBounds.c2 - selBounds.c1 + 1));
  };

  const doDeleteCols = () => apply((s) => deleteCols(s, selBounds.c1, selBounds.c2 - selBounds.c1 + 1));

  // ── render helpers ──────────────────────────────────────────────────────
  const numericish = (v: string) => v !== '' && /^[-$(]?[\d,]+(\.\d+)?[%)]?$/.test(v);

  const issueClass = (level: CellIssue['level']): string =>
    level === 'error'
      ? 'bg-destructive/10 shadow-[inset_2px_0_0_0_var(--destructive)]'
      : level === 'warning'
        ? 'bg-warning/15 shadow-[inset_2px_0_0_0_var(--warning)]'
        : level === 'success'
          ? 'bg-success/10'
          : 'bg-accent/60';

  const renderCell = (vr: number, dr: number, c: number, sticky: boolean) => {
    const v = getCell(sheet, dr, c);
    const selected = isSelected(vr, c);
    const active = vr === sel.ar && c === sel.ac;
    const isEdit = editing?.vr === vr && editing.c === c;
    const issue = decorations?.cells?.get(cellKey(dr, c));
    return (
      <div
        key={c}
        data-cell
        onMouseDown={(e) => cellMouseDown(e, vr, c)}
        onMouseEnter={() => cellMouseEnter(vr, c)}
        onDoubleClick={() => startEdit(vr, c)}
        onContextMenu={() => {
          setCtxTarget({ kind: 'cell', vr, c });
          if (!isSelected(vr, c)) moveTo(vr, c, false);
        }}
        title={issue?.message}
        className={cn(
          'relative flex-none overflow-hidden border-b border-r border-border/70 px-2 text-xs leading-[27px] whitespace-nowrap',
          sticky && 'sticky z-[5]',
          selected ? 'bg-primary/10' : sticky ? 'bg-background' : 'bg-card',
          issue && !selected && issueClass(issue.level),
          active && 'ring-2 ring-inset ring-primary',
          numericish(v) && 'text-right tabular-nums',
        )}
        style={{ width: widths[c], height: ROW_H, ...(sticky ? { left: ROW_NUM_W + (colX[c] as number) } : {}) }}
      >
        {isEdit ? (
          <input
            ref={editorRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={() => commitEdit()}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') {
                e.preventDefault();
                commitEdit(e.shiftKey ? 'up' : 'down');
              } else if (e.key === 'Tab') {
                e.preventDefault();
                commitEdit(e.shiftKey ? 'left' : 'right');
              } else if (e.key === 'Escape') {
                e.preventDefault();
                cancelEdit();
              }
            }}
            className="absolute inset-0 z-20 w-full border-2 border-primary bg-background px-1.5 text-xs outline-none"
          />
        ) : (
          v
        )}
      </div>
    );
  };

  const renderRow = (vr: number) => {
    const dr = viewToData(vr);
    const rowSelected = selBounds.c1 === 0 && selBounds.c2 === cols - 1 && vr >= selBounds.r1 && vr <= selBounds.r2;
    const mark = decorations?.rows?.get(dr);
    return (
      <div key={vr} className="absolute flex" style={{ top: headerBlockH + vr * ROW_H, left: 0, width: totalW, height: ROW_H }}>
        <div
          data-cell
          onMouseDown={(e) => {
            e.preventDefault();
            containerRef.current?.focus();
            if (editing) commitEdit();
            setSel(e.shiftKey ? (s) => ({ ...s, fr: vr, fc: cols - 1 }) : { ar: vr, ac: 0, fr: vr, fc: cols - 1 });
            setDragging(true);
          }}
          onMouseEnter={() => {
            if (dragging) setSel((s) => ({ ...s, fr: vr }));
          }}
          onContextMenu={() => {
            setCtxTarget({ kind: 'row', vr, c: 0 });
            if (!(vr >= selBounds.r1 && vr <= selBounds.r2)) setSel({ ar: vr, ac: 0, fr: vr, fc: cols - 1 });
          }}
          title={mark ? `${mark.label}${mark.message ? ` — ${mark.message}` : ''}` : undefined}
          className={cn(
            'sticky left-0 z-10 flex flex-none select-none items-center justify-between gap-1 border-b border-r border-border/70 px-1.5 text-[10px] leading-[27px] text-muted-foreground',
            rowSelected ? 'bg-primary/15 font-semibold text-foreground' : 'bg-muted/70',
          )}
          style={{ width: ROW_NUM_W, height: ROW_H }}
        >
          <span className="tabular-nums">{dr + 1}</span>
          {mark && (
            <span
              className={cn(
                'inline-block size-2 shrink-0',
                mark.level === 'error' ? 'bg-destructive' : mark.level === 'warning' ? 'bg-warning' : mark.level === 'success' ? 'bg-success' : 'bg-muted-foreground/50',
              )}
            />
          )}
        </div>
        {Array.from({ length: pinned }, (_, c) => renderCell(vr, dr, c, true))}
        {firstCol > pinned && <div className="flex-none" style={{ width: (colX[firstCol] as number) - (colX[pinned] as number) }} />}
        {Array.from({ length: Math.max(0, lastCol - Math.max(firstCol, pinned) + 1) }, (_, i) => renderCell(vr, dr, Math.max(firstCol, pinned) + i, false))}
      </div>
    );
  };

  const renderHeaderCell = (c: number, sticky: boolean) => {
    const colSelected = c >= selBounds.c1 && c <= selBounds.c2 && selBounds.r1 === 0 && selBounds.r2 === bodyRowCount - 1;
    const hasFilter = sheet.filters[c] !== undefined;
    return (
      <div
        key={c}
        onMouseDown={(e) => {
          if ((e.target as HTMLElement).closest('[data-noselect]')) return;
          e.preventDefault();
          containerRef.current?.focus();
          if (editing) commitEdit();
          setSel(e.shiftKey ? (s) => ({ ...s, fc: c, fr: bodyRowCount - 1 }) : { ar: 0, ac: c, fr: bodyRowCount - 1, fc: c });
        }}
        onContextMenu={() => {
          setCtxTarget({ kind: 'col', vr: 0, c });
          if (!(c >= selBounds.c1 && c <= selBounds.c2)) setSel({ ar: 0, ac: c, fr: bodyRowCount - 1, fc: c });
        }}
        onDoubleClick={(e) => {
          if ((e.target as HTMLElement).closest('[data-resize]')) apply((s) => autofitColWidth(s, c), { undoable: false });
        }}
        className={cn(
          'group/hc relative flex-none select-none border-b border-r border-border/70 text-center text-[11px] font-medium leading-[25px]',
          sticky ? 'sticky z-[25]' : '',
          colSelected ? 'bg-primary/15 text-foreground' : 'bg-muted text-muted-foreground',
        )}
        style={{ width: widths[c], height: HEADER_H, ...(sticky ? { left: ROW_NUM_W + (colX[c] as number) } : {}) }}
      >
        {colLabel(c)}
        <FilterButton
          col={c}
          sheet={sheet}
          active={hasFilter}
          onApply={(values) =>
            apply(
              (s) => {
                const filters = { ...s.filters };
                if (values === null) delete filters[c];
                else filters[c] = values;
                return { ...s, filters };
              },
              { undoable: false },
            )
          }
        />
        <div
          data-resize
          onMouseDown={(e) => startResize(e, c)}
          onDoubleClick={(e) => {
            e.stopPropagation();
            apply((s) => autofitColWidth(s, c), { undoable: false });
          }}
          className="absolute top-0 right-0 z-10 h-full w-1.5 cursor-col-resize hover:bg-primary/60"
        />
      </div>
    );
  };

  const renderBandCell = (c: number, sticky: boolean) => (
    <div
      key={c}
      data-noselect
      className={cn('flex-none overflow-hidden border-b border-r border-border/70 bg-muted/40', sticky && 'sticky z-[25] bg-muted/80')}
      style={{ width: widths[c], height: bandH, ...(sticky ? { left: ROW_NUM_W + (colX[c] as number) } : {}) }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {columnBand?.render(c, widths[c] as number)}
    </div>
  );

  const headerRow = (
    <div className="flex" style={{ width: totalW, height: HEADER_H }}>
      <div
        onMouseDown={(e) => {
          e.preventDefault();
          containerRef.current?.focus();
          setSel({ ar: 0, ac: 0, fr: bodyRowCount - 1, fc: cols - 1 });
        }}
        className="sticky left-0 z-30 flex-none cursor-pointer border-b border-r border-border/70 bg-muted"
        style={{ width: ROW_NUM_W, height: HEADER_H }}
        title="Select all"
      />
      {Array.from({ length: pinned }, (_, c) => renderHeaderCell(c, true))}
      {firstCol > pinned && <div className="flex-none" style={{ width: (colX[firstCol] as number) - (colX[pinned] as number) }} />}
      {Array.from({ length: Math.max(0, lastCol - Math.max(firstCol, pinned) + 1) }, (_, i) => renderHeaderCell(Math.max(firstCol, pinned) + i, false))}
    </div>
  );

  const bandRow = columnBand ? (
    <div className="flex" style={{ width: totalW, height: bandH }}>
      <div className="sticky left-0 z-30 flex-none border-b border-r border-border/70 bg-muted" style={{ width: ROW_NUM_W, height: bandH }} />
      {Array.from({ length: pinned }, (_, c) => renderBandCell(c, true))}
      {firstCol > pinned && <div className="flex-none" style={{ width: (colX[firstCol] as number) - (colX[pinned] as number) }} />}
      {Array.from({ length: Math.max(0, lastCol - Math.max(firstCol, pinned) + 1) }, (_, i) => renderBandCell(Math.max(firstCol, pinned) + i, false))}
    </div>
  ) : null;

  function renderFrozenHeaderCell(c: number, sticky: boolean) {
    const v = getCell(sheet, 0, c);
    return (
      <div
        key={c}
        className={cn('flex-none overflow-hidden whitespace-nowrap border-b border-r border-border/70 bg-muted/50 px-2 text-xs font-semibold leading-[27px]', sticky && 'sticky z-[5] bg-muted/80')}
        style={{ width: widths[c], height: ROW_H, ...(sticky ? { left: ROW_NUM_W + (colX[c] as number) } : {}) }}
        title={v}
      >
        {v}
      </div>
    );
  }

  const frozenHeaderRow =
    headerDataRow === null ? null : (
      <div className="flex" style={{ width: totalW, height: ROW_H }}>
        <div className="sticky left-0 z-10 flex-none border-b border-r border-border/70 bg-muted/70 text-center text-[10px] leading-[27px] text-muted-foreground" style={{ width: ROW_NUM_W, height: ROW_H }}>
          1
        </div>
        {Array.from({ length: pinned }, (_, c) => renderFrozenHeaderCell(c, true))}
        {firstCol > pinned && <div className="flex-none" style={{ width: (colX[firstCol] as number) - (colX[pinned] as number) }} />}
        {Array.from({ length: Math.max(0, lastCol - Math.max(firstCol, pinned) + 1) }, (_, i) => renderFrozenHeaderCell(Math.max(firstCol, pinned) + i, false))}
      </div>
    );

  const visibleBodyRows: number[] = [];
  for (let vr = firstRow; vr <= lastRow; vr++) visibleBodyRows.push(vr);

  // ── formula bar ─────────────────────────────────────────────────────────
  const [fxValue, setFxValue] = useState(activeValue);
  const fxEditingRef = useRef(false);
  useEffect(() => {
    if (!fxEditingRef.current) setFxValue(activeValue);
  }, [activeValue, sel.ar, sel.ac]);

  const commitFx = () => {
    fxEditingRef.current = false;
    if (fxValue !== activeValue && !readOnly) apply((s) => setCell(s, activeData.r, activeData.c, fxValue));
    containerRef.current?.focus();
  };

  const activeIssue = decorations?.cells?.get(cellKey(activeData.r, activeData.c));

  return (
    <div className={cn('flex h-full min-h-0 flex-col', className)}>
      {/* formula bar */}
      <div className="flex items-center gap-1.5 border-b bg-muted/30 px-2 py-1">
        <div className="w-16 shrink-0 border bg-background px-2 py-0.5 text-center font-mono text-[11px] text-muted-foreground">{cellRef(activeData.r, activeData.c)}</div>
        <span className="select-none font-display text-xs italic text-muted-foreground">fx</span>
        <input
          value={fxValue}
          readOnly={readOnly}
          onFocus={() => {
            fxEditingRef.current = true;
          }}
          onChange={(e) => setFxValue(e.target.value)}
          onBlur={commitFx}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitFx();
              moveTo(sel.ar + 1, sel.ac, false);
            }
            if (e.key === 'Escape') {
              fxEditingRef.current = false;
              setFxValue(activeValue);
              containerRef.current?.focus();
            }
          }}
          className="h-6 min-w-0 flex-1 border bg-background px-2 font-mono text-xs outline-none focus:ring-1 focus:ring-primary"
          placeholder={readOnly ? '' : 'Cell value'}
        />
        {activeIssue && (
          <span className={cn('max-w-[40%] truncate text-[11px]', activeIssue.level === 'error' ? 'text-destructive' : 'text-muted-foreground')} title={activeIssue.message}>
            {activeIssue.message}
          </span>
        )}
        <Button variant="ghost" size="xs" onClick={openFind}>
          <SearchIcon className="size-3" /> Find
        </Button>
      </div>

      {/* find & replace */}
      {find.open && (
        <div className="flex flex-wrap items-center gap-1.5 border-b bg-muted/40 px-2 py-1.5">
          <Input
            ref={findInputRef}
            value={find.q}
            onChange={(e) => setFind((f) => ({ ...f, q: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') findNext(e.shiftKey);
              if (e.key === 'Escape') setFind((f) => ({ ...f, open: false }));
            }}
            placeholder="Find…"
            className="h-7 w-44 text-xs"
          />
          <Input
            value={find.rep}
            onChange={(e) => setFind((f) => ({ ...f, rep: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') replaceCurrent();
            }}
            placeholder="Replace with…"
            className="h-7 w-44 text-xs"
            disabled={readOnly}
          />
          <Button variant="outline" size="xs" onClick={() => findNext(false)}>
            Next
          </Button>
          <Button variant="outline" size="xs" onClick={() => findNext(true)}>
            Prev
          </Button>
          {!readOnly && (
            <>
              <Button variant="outline" size="xs" onClick={replaceCurrent}>
                <ReplaceIcon className="size-3" /> Replace
              </Button>
              <Button variant="outline" size="xs" onClick={replaceAll}>
                Replace all
              </Button>
            </>
          )}
          <label className="flex cursor-pointer items-center gap-1 text-[11px] text-muted-foreground">
            <Checkbox checked={find.matchCase} onCheckedChange={(v) => setFind((f) => ({ ...f, matchCase: v === true }))} className="size-3.5" />
            Match case
          </label>
          <Button variant="ghost" size="icon-sm" className="ml-auto size-7" onClick={() => setFind((f) => ({ ...f, open: false }))} aria-label="Close find">
            <XIcon className="size-3.5" />
          </Button>
        </div>
      )}

      {/* the grid */}
      <ContextMenu
        onOpenChange={(open) => {
          if (!open) setCtxTarget(null);
        }}
      >
        <ContextMenuTrigger asChild>
          <div
            ref={containerRef}
            tabIndex={0}
            className="relative min-h-0 flex-1 select-none overflow-auto overscroll-contain bg-muted/20 outline-none"
            onScroll={(e) => {
              const el = e.currentTarget;
              setScroll({ top: el.scrollTop, left: el.scrollLeft });
            }}
            onKeyDown={onKeyDown}
            onCopy={copySelection}
            onCut={(e) => {
              copySelection(e);
              clearSelection();
            }}
            onPaste={(e) => {
              e.preventDefault();
              pasteGrid(e.clipboardData.getData('text/plain') ?? e.clipboardData.getData('text'));
            }}
          >
            <div className="relative" style={{ width: totalW, height: totalH }}>
              <div className="sticky top-0 z-30" style={{ width: totalW }}>
                {headerRow}
                {bandRow}
                {frozenHeaderRow}
              </div>
              {visibleBodyRows.map(renderRow)}
            </div>
          </div>
        </ContextMenuTrigger>
        <ContextMenuContent className="w-56">
          {!readOnly && ctxTarget?.kind !== 'col' && (
            <>
              <ContextMenuItem onClick={() => doInsertRows('above')}>
                <BetweenHorizonalStartIcon /> Insert row{selBounds.r2 > selBounds.r1 ? 's' : ''} above
              </ContextMenuItem>
              <ContextMenuItem onClick={() => doInsertRows('below')}>
                <BetweenHorizonalStartIcon className="rotate-180" /> Insert row{selBounds.r2 > selBounds.r1 ? 's' : ''} below
              </ContextMenuItem>
              <ContextMenuItem variant="destructive" onClick={doDeleteRows}>
                <Trash2Icon /> Delete row{selBounds.r2 > selBounds.r1 ? 's' : ''}
              </ContextMenuItem>
              <ContextMenuSeparator />
            </>
          )}
          {ctxTarget?.kind !== 'row' && (
            <>
              {!readOnly && (
                <>
                  <ContextMenuItem onClick={() => doInsertCols('left')}>
                    <BetweenVerticalStartIcon /> Insert column{selBounds.c2 > selBounds.c1 ? 's' : ''} left
                  </ContextMenuItem>
                  <ContextMenuItem onClick={() => doInsertCols('right')}>
                    <BetweenVerticalStartIcon className="rotate-180" /> Insert column{selBounds.c2 > selBounds.c1 ? 's' : ''} right
                  </ContextMenuItem>
                  <ContextMenuItem variant="destructive" onClick={doDeleteCols}>
                    <Trash2Icon /> Delete column{selBounds.c2 > selBounds.c1 ? 's' : ''}
                  </ContextMenuItem>
                  <ContextMenuSeparator />
                </>
              )}
              <ContextMenuItem onClick={() => apply((s) => sortByColumn(s, sel.ac, 'asc'))}>
                <ArrowDownAZIcon /> Sort A → Z
              </ContextMenuItem>
              <ContextMenuItem onClick={() => apply((s) => sortByColumn(s, sel.ac, 'desc'))}>
                <ArrowUpAZIcon /> Sort Z → A
              </ContextMenuItem>
              <ContextMenuSeparator />
              {sel.ac + 1 === pinned ? (
                <ContextMenuItem onClick={() => apply((s) => ({ ...s, pinnedCols: 0 }), { undoable: false })}>
                  <PinOffIcon /> Unpin columns
                </ContextMenuItem>
              ) : (
                <ContextMenuItem onClick={() => apply((s) => ({ ...s, pinnedCols: sel.ac + 1 }), { undoable: false })}>
                  <PinIcon /> Pin columns to here ({colLabel(sel.ac)})
                </ContextMenuItem>
              )}
              {pinned > 0 && sel.ac + 1 !== pinned && (
                <ContextMenuItem onClick={() => apply((s) => ({ ...s, pinnedCols: 0 }), { undoable: false })}>
                  <PinOffIcon /> Unpin all columns
                </ContextMenuItem>
              )}
              <ContextMenuItem onClick={() => apply((s) => autofitColWidth(s, sel.ac), { undoable: false })}>
                <RulerIcon /> Autofit column width
              </ContextMenuItem>
              <ContextMenuSeparator />
            </>
          )}
          {!readOnly && (
            <ContextMenuItem onClick={clearSelection}>
              <EraserIcon /> Clear contents
            </ContextMenuItem>
          )}
          {Object.keys(sheet.filters).length > 0 && (
            <ContextMenuItem onClick={() => apply((s) => ({ ...s, filters: {} }), { undoable: false })}>
              <CopyXIcon /> Clear all filters
            </ContextMenuItem>
          )}
        </ContextMenuContent>
      </ContextMenu>
    </div>
  );
});

// ── column filter popover ───────────────────────────────────────────────────

function FilterButton({ col, sheet, active, onApply }: { col: number; sheet: SheetData; active: boolean; onApply: (values: string[] | null) => void }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [all, setAll] = useState<{ values: string[]; truncated: boolean }>({ values: [], truncated: false });

  useEffect(() => {
    if (!open) return;
    const vals = columnValues(sheet, col);
    setAll(vals);
    setChecked(new Set(sheet.filters[col] ?? vals.values));
    setSearch('');
  }, [open, sheet, col]);

  const visible = all.values.filter((v) => !search || v.toLowerCase().includes(search.toLowerCase()));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-noselect
          onMouseDown={(e) => e.stopPropagation()}
          className={cn('absolute top-1/2 right-2 -translate-y-1/2 p-0.5 transition-opacity', active ? 'text-primary opacity-100' : 'opacity-0 hover:bg-muted group-hover/hc:opacity-100')}
          title={active ? 'Filtered — click to edit' : 'Filter this column'}
          aria-label={`Filter column ${colLabel(col)}`}
        >
          {active ? <ListFilterIcon className="size-3" /> : <ChevronDownIcon className="size-3" />}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-60 p-2" onMouseDown={(e) => e.stopPropagation()}>
        <p className="mb-1.5 flex items-center gap-1.5 text-xs font-medium">
          <FilterIcon className="size-3 text-primary" />
          Filter {colLabel(col)}
        </p>
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search values…" className="mb-1.5 h-7 text-xs" />
        <div className="mb-1.5 flex gap-3 text-[11px]">
          <button type="button" className="text-primary hover:underline" onClick={() => setChecked(new Set(all.values))}>
            Select all
          </button>
          <button type="button" className="text-primary hover:underline" onClick={() => setChecked(new Set())}>
            Clear
          </button>
          {all.truncated && <span className="ml-auto text-muted-foreground">first 1,000 values</span>}
        </div>
        <div className="max-h-52 space-y-0.5 overflow-y-auto">
          {visible.map((v) => (
            <label key={v} className="flex cursor-pointer items-center gap-2 px-1 py-0.5 text-xs hover:bg-muted/60">
              <Checkbox
                className="size-3.5"
                checked={checked.has(v)}
                onCheckedChange={(on) =>
                  setChecked((prev) => {
                    const next = new Set(prev);
                    if (on === true) next.add(v);
                    else next.delete(v);
                    return next;
                  })
                }
              />
              <span className="truncate">{v === '' ? <em className="text-muted-foreground">(blank)</em> : v}</span>
            </label>
          ))}
          {visible.length === 0 && <p className="py-3 text-center text-[11px] text-muted-foreground">No values match.</p>}
        </div>
        <div className="mt-2 flex justify-between gap-2">
          <Button
            variant="ghost"
            size="xs"
            disabled={!active}
            onClick={() => {
              onApply(null);
              setOpen(false);
            }}
          >
            Clear filter
          </Button>
          <Button
            size="xs"
            disabled={checked.size === 0}
            onClick={() => {
              onApply(checked.size === all.values.length ? null : [...checked]);
              setOpen(false);
            }}
          >
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
